import { useMarkExclusiveInfoSeen } from '~/api/mutations/user/useMarkExclusiveInfoSeen';

export type ExclusiveInfoTrigger = 'first_tap' | 'info_icon';

// Signed-out visitors (who never see the chip today) fall back to once per browser.
const GUEST_SEEN_KEY = 'exclusiveInfoSeenAt';

const readGuestSeen = (): boolean => {
  try {
    return !!window.localStorage.getItem(GUEST_SEEN_KEY);
  } catch {
    return false;
  }
};

const writeGuestSeen = () => {
  try {
    window.localStorage.setItem(GUEST_SEEN_KEY, new Date().toISOString());
  } catch {
    // Storage blocked: the modal may show again, nothing else breaks.
  }
};

// The 100% Commission info modal (ID-118): opens automatically the first time an account turns the chip on,
// and on demand from the "i" button. Seen state is per account (User.exclusiveInfoSeenAt).
export const useExclusiveInfo = () => {
  const authStore = useAuthStore();
  const { sendEventToBackend } = useTracking();
  const { mutate: markSeen } = useMarkExclusiveInfoSeen();

  const isOpen = ref(false);
  // Covers the gap before the POST lands in the store.
  const shownThisPage = ref(false);
  const hasSeen = computed(
    () => shownThisPage.value || (authStore.token ? !!authStore.user?.exclusiveInfoSeenAt : readGuestSeen()),
  );

  const rememberSeen = () => {
    if (hasSeen.value) return;
    if (authStore.token) markSeen();
    else writeGuestSeen();
  };

  const open = (trigger: ExclusiveInfoTrigger) => {
    isOpen.value = true;
    rememberSeen();
    shownThisPage.value = true;
    void sendEventToBackend('exclusive_info_viewed', { trigger });
  };

  const openIfFirstTime = () => {
    if (!hasSeen.value) open('first_tap');
  };

  const close = () => {
    isOpen.value = false;
  };

  return {
    isOpen: readonly(isOpen),
    open,
    openIfFirstTime,
    close,
  };
};
